import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const AdminAuthContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AdminAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('mr_admin_token'));
  const [email, setEmail] = useState(() => localStorage.getItem('mr_admin_email'));
  const [loading, setLoading] = useState(false);
  // Feb 2026 — Cache is_super flag from `/admin/me` so downstream
  // components can render Super Admin Override affordances without a
  // per-mount API roundtrip.
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // Feb 2026 — Warehouse assignment surfaced by `/admin/me`. `warehouses`
  // is `[{id, name, is_default}]` resolved server-side, so the header,
  // profile card and Stock Upload dropdown can render names directly.
  // `warehouseScopeAll` is true for super admin + any sub-admin whose
  // scope is `["*"]` — they see every warehouse in the list.
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseScopeAll, setWarehouseScopeAll] = useState(false);
  // Feb 2026 — Duty Hours / Off-duty Days gate. Populated from
  // /admin/me alongside is_super. `null` = still loading; `{}` =
  // fetched but user has no window (unrestricted access).
  const [dutyMeta, setDutyMeta] = useState(null);
  // Feb 2026 — Attendance greeting payload. Set by `login()` when the
  // backend fires the first-of-the-day dialog for the user, cleared
  // by AttendanceDialog when the user dismisses it. Persisted in
  // sessionStorage so a hard refresh right after login still shows
  // the dialog (survives token restore but not tab close).
  const [attendance, setAttendance] = useState(() => {
    try {
      const raw = sessionStorage.getItem('mr_admin_attendance');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  useEffect(() => {
    let cancelled = false;
    if (!token) { setIsSuperAdmin(false); setDutyMeta(null); return; }
    const fetchMe = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setIsSuperAdmin(!!data?.is_super_admin);
          setWarehouses(Array.isArray(data?.warehouses) ? data.warehouses : []);
          setWarehouseScopeAll(!!data?.warehouse_scope_all);
          setDutyMeta({
            duty_start: data?.duty_start || '',
            duty_end: data?.duty_end || '',
            duty_days: Array.isArray(data?.duty_days) ? data.duty_days : [],
            off_duty_days: Array.isArray(data?.off_duty_days) ? data.off_duty_days : [],
            email: data?.email || '',
            name: data?.name || '',
            // Feb 2026 — Time-windowed overtime access. When true
            // the DutyGate lets the user through even outside their
            // duty window. Server-authoritative — the frontend never
            // computes this locally.
            in_overtime_window: !!data?.in_overtime_window,
            // Feb 2026 — Active overtime slot window (`{date,
            // start_time, end_time}`) so DutyGate can count down
            // expiry locally to the second between /admin/me polls.
            overtime_window: (data?.overtime_window && typeof data.overtime_window === 'object')
              ? data.overtime_window
              : {},
            // Feb 2026 — Server-authoritative on-duty flag. Sole
            // source of truth for the DutyGate; the client no
            // longer computes on/off from the viewer's system
            // clock (which can be wrong or tampered with). Default
            // to `true` when the field isn't present so a legacy
            // backend doesn't lock every admin out.
            on_duty: (data && Object.prototype.hasOwnProperty.call(data, 'on_duty'))
              ? !!data.on_duty
              : true,
            // Feb 2026 — Fail-closed flag. When the /admin/me poll
            // errors we set this so DutyGate can lock the shell
            // instead of failing OPEN and leaking the dashboard.
            fetch_failed: false,
          });
        }
      } catch {
        if (!cancelled) {
          // Feb 2026 — Do NOT clear `isSuperAdmin` here. A temporary
          // network error must never discard the user's super-admin
          // status; otherwise a mid-session Wi-Fi blip locks the
          // super admin out of the shell the way normal users are.
          // Preserve whatever we had (so a mid-session network blip
          // doesn't drop the whole gate to `null` = "loading"),
          // but flag the fetch as failed so DutyGate fails CLOSED
          // rather than showing the dashboard. First-load blips
          // leave `dutyMeta` null so the gate stays put.
          setDutyMeta((prev) => (prev
            ? { ...prev, fetch_failed: true, in_overtime_window: false, on_duty: false }
            : { duty_start: '', duty_end: '', duty_days: [], off_duty_days: [], email: '', name: '', in_overtime_window: false, overtime_window: {}, on_duty: false, fetch_failed: true }
          ));
        }
      }
    };
    fetchMe();
    // Re-poll /admin/me every 60s so the `in_overtime_window` flag
    // flips within a minute of the approved slot opening or closing.
    const id = setInterval(fetchMe, 60 * 1000);
    // Feb 2026 — Immediate re-check when the tab regains focus or
    // becomes visible again. Users who leave the tab open all day
    // otherwise see the gate flip up to 60s late.
    const onFocus = () => { fetchMe(); };
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchMe();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token]);

  useEffect(() => {
    if (token) {
      localStorage.setItem('mr_admin_token', token);
    } else {
      localStorage.removeItem('mr_admin_token');
    }
  }, [token]);

  useEffect(() => {
    if (email) {
      localStorage.setItem('mr_admin_email', email);
    } else {
      localStorage.removeItem('mr_admin_email');
    }
  }, [email]);

  const login = async (loginEmail, password) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, {
        email: loginEmail,
        password,
      });
      setToken(data.access_token);
      setEmail(data.email);
      // Feb 2026 — Attendance dialog payload from the backend (only
      // present on the FIRST password login of the Muscat day for
      // sub-admins with a duty schedule). Cache in sessionStorage so
      // a race between setState + AdminLayout mount can't lose it.
      if (data.attendance && data.attendance.show) {
        setAttendance(data.attendance);
        try { sessionStorage.setItem('mr_admin_attendance', JSON.stringify(data.attendance)); } catch { /* quota */ }
      }
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.response?.data?.detail || 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  const applyToken = (accessToken, loginEmail) => {
    setToken(accessToken);
    if (loginEmail) setEmail(loginEmail);
  };

  const logout = () => {
    setToken(null);
    setEmail(null);
    setAttendance(null);
    try { sessionStorage.removeItem('mr_admin_attendance'); } catch { /* ignore */ }
  };

  const dismissAttendance = () => {
    setAttendance(null);
    try { sessionStorage.removeItem('mr_admin_attendance'); } catch { /* ignore */ }
  };

  // Oct 2026 — MUST stay memoised on `token`. This object is used as a
  // useEffect dependency in ~59 places across the admin app. The /admin/me
  // poll above re-renders this provider every 60s (and on every tab focus),
  // so an inline object literal here handed every one of those effects a
  // fresh identity each minute and re-ran them. In ManualAllocateModal that
  // re-ran the available-sources loader and re-seeded `allocations`, wiping
  // every quantity already typed — the "filled lines disappear" bug.
  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  // Oct 2026 — Memoise the context value too, so consumers depending on the
  // whole context object don't re-render on every /admin/me poll either.
  const ctxValue = useMemo(
    () => ({ token, email, login, applyToken, logout, loading, authHeaders, isSuperAdmin, dutyMeta, attendance, dismissAttendance, warehouses, warehouseScopeAll }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, email, loading, authHeaders, isSuperAdmin, dutyMeta, attendance, warehouses, warehouseScopeAll],
  );

  return (
    <AdminAuthContext.Provider value={ctxValue}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => useContext(AdminAuthContext);
